import { useCallback, useEffect, useState } from 'react'

import { config } from '@/config'
import { useMessage, useTranslation, useUserTmpUploader } from '@/core'
import { Camera } from '@/core/native'

import { ConsoleImageEditModal } from './ConsoleImageEditModal'

export const useConsoleImageEditor = ({
  artistId,
  callback,
  initialPath = '',
}: {
  artistId: string
  callback: (data: { tmpPath: string; fileUrl: string }) => Promise<void>
  initialPath?: string | undefined
}) => {
  const { t } = useTranslation()
  const { showError } = useMessage()
  const [open, setOpen] = useState(false)

  const { getImage } = Camera
  const { upload } = useUserTmpUploader(artistId)
  const [filePathBeforeCrop, setFilePathBeforeCrop] = useState('')
  const [filePathAfterCrop, setFilePathAfterCrop] = useState('')

  const handleGetImage = useCallback(
    () =>
      getImage(config.upload.mimeTypes.image)
        .then(file => {
          if (file.size > config.upload.limit.image) {
            throw new Error('uploadErrorMaximumSize')
          }
          return file
        })
        .catch(error => {
          if (error.message === 'uploadErrorMaximumSize') {
            showError(new Error(t('uploadErrorMaximumSize')))
            return
          }
          if (error.message === 'uploadErrorImageOnly') {
            showError(new Error(t('uploadErrorImageOnly')))
            return
          }
          showError(error)
          return
        }),
    [getImage, showError, t]
  )

  const handleChoseFile = useCallback(async () => {
    const file = await handleGetImage()
    if (!file) return
    setFilePathBeforeCrop(URL.createObjectURL(file))
    setOpen(true)
  }, [handleGetImage])

  const handleRemoveFile = useCallback(() => {
    setFilePathAfterCrop('')
  }, [])

  const handleSave = async (data: { tmpPath: string; fileUrl: string }) => {
    setFilePathAfterCrop(data.fileUrl)
    await callback(data)
    setOpen(false)
  }

  useEffect(() => {
    setFilePathAfterCrop(initialPath)
  }, [initialPath])

  const ModalProps = {
    open,
    artistId,
    src: filePathBeforeCrop ?? '',
    onClose: () => setOpen(false),
    onUploadFile: (file: Blob | File) => upload(file),
    onSave: handleSave,
  }

  return {
    ConsoleImageEditModal,
    ModalProps,
    handleChoseFile,
    handleRemoveFile,
    filePathAfterCrop,
  }
}
